import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/cn';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

/**
 * Кнопки повторяют стиль основного сайта клуба: прямоугольные,
 * с прописными буквами и увеличенным межбуквенным интервалом.
 */
const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-[var(--accent)] text-[var(--accent-contrast)] border border-[var(--accent)] hover:bg-[var(--accent-hover)] hover:border-[var(--accent-hover)]',
  secondary:
    'bg-[var(--surface-card)] text-[var(--text-primary)] border border-[var(--border-strong)] hover:border-[var(--accent)] hover:text-[var(--accent)]',
  /** Прозрачная кнопка с тонкой рамкой — вариант поверх изображений. */
  outline: 'bg-transparent text-current border border-current/60 hover:border-current hover:bg-current/5',
  ghost:
    'bg-transparent text-[var(--text-secondary)] border border-transparent hover:bg-[var(--surface-muted)] hover:text-[var(--text-primary)]',
  danger: 'bg-danger-500 text-white border border-danger-500 hover:bg-danger-600 hover:border-danger-600',
};

const SIZES: Record<Size, string> = {
  // Минимум 44px по высоте на всех размерах: это нижняя граница удобного касания.
  sm: 'h-11 px-4 text-xs',
  md: 'h-12 px-6 text-[13px]',
  lg: 'h-14 px-8 text-sm',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, fullWidth = false, className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      // Кнопка во время загрузки остаётся в потоке фокуса, но не срабатывает
      // повторно: это предотвращает двойную оплату по двойному клику.
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-[var(--radius-control)]',
        'font-medium uppercase tracking-[0.08em] whitespace-nowrap',
        'transition-colors duration-150 select-none',
        'disabled:cursor-not-allowed disabled:opacity-55',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading && <Spinner className="size-4" />}
      {children}
    </button>
  );
});
